import moment from "moment";

/******************************************************************************************** */
/* Date/time helper functions                                                                 */
/******************************************************************************************** */
export const isDefaultDate = (date) => moment(date).format("YYYY") === "0001";

const LOCAL_DATE_FORMAT = "YYYY-MM-DD";
const LOCAL_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** Today's calendar date in the user's local timezone (YYYY-MM-DD). */
export const getLocalDateValue = () => moment().format(LOCAL_DATE_FORMAT);

/**
 * Convert a DatePicker moment/Date into a local calendar date string.
 * Avoids .utc().format() which shifts the day for NZ (and other UTC+ offsets).
 */
export const fromLocalDatePicker = (value) =>
	value ? moment(value).format(LOCAL_DATE_FORMAT) : null;

/**
 * Parse a stored date for DatePicker display using the local calendar day.
 */
export const toLocalDatePickerValue = (date) => {
	if (!date || isDefaultDate(date)) {
		return null;
	}
	const str = String(date).trim();
	if (LOCAL_DATE_ONLY.test(str)) {
		return moment(str, LOCAL_DATE_FORMAT);
	}
	const parsed = moment(date);
	return parsed.isValid() ? parsed.local().startOf("day") : null;
};

/**
 * Convert a local calendar date to a Date for the API.
 * Uses local noon so UTC serialization keeps the same calendar day for NZ (UTC+12/+13).
 */
export const localDateToApiDate = (date) => {
	if (!date) {
		return moment().startOf("day").hour(12).toDate();
	}
	const str = String(date).trim();
	const parsed = LOCAL_DATE_ONLY.test(str)
		? moment(str, LOCAL_DATE_FORMAT)
		: moment(date);
	if (!parsed.isValid()) {
		return moment().startOf("day").hour(12).toDate();
	}
	return parsed.local().startOf("day").hour(12).toDate();
};

export const formatDate = (datetime) => {
	if (
		!datetime ||
		datetime === "" ||
		moment(datetime).format("YYYY") === "0001"
	) {
		return "-";
	}
	const str = String(datetime).trim();
	// Date-only values are calendar dates in local time — do not treat as UTC midnight
	if (LOCAL_DATE_ONLY.test(str)) {
		return moment(str, LOCAL_DATE_FORMAT).format("Do MMMM YYYY");
	}
	if (moment(datetime).isValid()) {
		return moment.utc(datetime).local().format("Do MMMM YYYY"); // Convert from UTC to local time
	}
	return moment.utc(datetime, "MMM DD YYYY HH:mmAA").local().format("Do MMMM YYYY"); // Convert from UTC to local time
};

export const formatDateTime = (datetime) => {
	let format = "h:mm a, D MMM YYYY";
	if (!datetime) {
		return "-";
	}
	if (moment(datetime).isValid()) {
		// For leave records, we want to ensure the date is displayed correctly
		// Convert UTC to local time (browser's timezone) before formatting
		// Use moment.utc() to parse the UTC datetime, then convert to local
		return moment.utc(datetime).local().format(format);
	}
	return moment.utc(datetime, "MMM DD YYYY HH:mmAA").local().format(format);
};

export const formatTime = (time) => {
	if (!time || time === "") {
		return "-";
	}
	if (moment(time).isValid()) {
		return moment.utc(time, ["HH:mm"]).local().format("h:mm a"); // Convert from UTC to local time
	}
	if (/^([01]\d|2[0-3]):?([0-5]\d)$/g.test(time)) {
		//For new entry formatted HH:mm
		return moment.utc(time, "HH:mm").local().format("h:mm a"); // Convert from UTC to local time
	}
	return moment.utc(time, "MMM DD YYYY HH:mmAA").local().format("h:mm a"); // Convert from UTC to local time
};

// used in add/edit form post effects for geolocation timestamp
export const getCurrentTimestamp = () => moment().format("D MMM YYYY h:mm A");

export const dayDifference = (startDate, endDate) => {
	let start = moment(startDate);
	let end = moment(endDate);

	return end.diff(start, "days");
};
